import React from 'react';
import { Screen } from '../App';
import { User } from '../storage';
import { BOOKING_LINK } from '../config';

interface Props { navigate: (s: Screen) => void; user: User | null; }

// A 90-second walkthrough of Decode My Student for school leaders. Public page at
// /tour. Clean mockups in the app's style (no screenshot tooling in the repo),
// gender-neutral language for teachers, and no em dashes.

const TIERS: { students: string; price: string }[] = [
  { students: 'Up to 300 students', price: '$6,000 / year' },
  { students: 'Up to 600 students', price: '$12,000 / year' },
  { students: 'Up to 900 students', price: '$18,000 / year' },
  { students: 'Up to 1,200 students', price: '$24,000 / year' },
  { students: 'More than 1,200 students', price: 'Contact us' },
];

// A small labeled "screen" mockup, styled like the app.
function Mock({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ background: 'var(--white)', border: '1px solid #ece7df', borderRadius: 12, boxShadow: 'var(--shadow-sm)', padding: 14 }}>
      {children}
    </div>
  );
}

const STEPS: { n: number; title: string; body: string; mock: React.ReactNode }[] = [
  {
    n: 1,
    title: 'A teacher describes what they are seeing',
    body: 'Initials and grade only. No names, no birthdates. Type it or say it out loud in a sentence or two.',
    mock: (
      <Mock>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <span className="mono" style={{ fontSize: 16, fontWeight: 600, color: 'var(--teal)' }}>J.T.</span>
          <span style={{ fontSize: 12, background: 'var(--teal-light)', color: 'var(--teal)', padding: '2px 8px', borderRadius: 12, fontWeight: 600 }}>4th grade</span>
        </div>
        <p style={{ fontSize: 13, color: 'var(--charcoal)', fontStyle: 'italic', lineHeight: 1.5 }}>
          "They shut down and refuse to move during transitions, then it turns into a standoff."
        </p>
      </Mock>
    ),
  },
  {
    n: 2,
    title: 'The app explains the brain science behind the behavior',
    body: 'Plain language, no jargon and no diagnosis. What the nervous system is likely doing, and why the behavior makes sense.',
    mock: (
      <Mock>
        <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, color: 'var(--teal)', marginBottom: 6 }}>THE BRAIN SCIENCE</p>
        <p style={{ fontSize: 13, color: 'var(--charcoal)', lineHeight: 1.6 }}>
          Transitions ask the brain to stop one thing and start another. For a student whose stress response is already high, that gap can feel unsafe, so the body braces and freezes. The standoff is protection, not defiance.
        </p>
      </Mock>
    ),
  },
  {
    n: 3,
    title: 'Something concrete to try in the next class',
    body: 'Specific, doable moves for the very next lesson, matched to what the teacher described.',
    mock: (
      <Mock>
        <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, color: 'var(--teal)', marginBottom: 8 }}>TRY IN THE NEXT CLASS</p>
        {[
          'Give a two minute heads up before each transition.',
          'Offer a small first step and a choice: "Start with question 1 or question 3?"',
        ].map((line, i) => (
          <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 8 }}>
            <span style={{ width: 20, height: 20, borderRadius: '50%', background: 'var(--amber)', color: '#fff', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{i + 1}</span>
            <p style={{ fontSize: 13, color: 'var(--charcoal)', lineHeight: 1.5 }}>{line}</p>
          </div>
        ))}
      </Mock>
    ),
  },
  {
    n: 4,
    title: 'A communication helper for parents and colleagues',
    body: 'Warm, ready to use language for a family message or a note to a colleague, so the teacher is never stuck on how to say it.',
    mock: (
      <Mock>
        <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, color: 'var(--teal)', marginBottom: 6 }}>MESSAGE TO A FAMILY</p>
        <div style={{ background: 'var(--teal-light)', borderRadius: 8, padding: '10px 12px' }}>
          <p style={{ fontSize: 13, color: 'var(--charcoal)', lineHeight: 1.6, fontStyle: 'italic' }}>
            "I want to partner with you on transitions. Here is one thing we are trying at school, and I would love to hear what works at home."
          </p>
        </div>
      </Mock>
    ),
  },
];

const NOT_LIST = [
  'No diagnosis',
  'No labels',
  'No student names or birthdates',
  'Built with FERPA and Vermont Act 156 in mind',
];

export default function Tour({ navigate, user }: Props) {
  const home: Screen = user ? 'dashboard' : 'landing';

  return (
    <div>
      <div className="top-bar">
        <button onClick={() => navigate(home)} style={{ background: 'none', color: 'var(--teal)', display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 600, border: 'none', cursor: 'pointer' }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18"><polyline points="15,18 9,12 15,6" /></svg>Back
        </button>
        <span className="top-bar-logo" style={{ fontSize: 15 }}>Decode My Student</span>
        <div style={{ width: 60 }} />
      </div>

      <div className="page-content" style={{ paddingTop: 28 }}>
        {/* Who it is for */}
        <h1 style={{ fontSize: 26, marginBottom: 8 }}>A 90 second walkthrough</h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: 15, lineHeight: 1.6, marginBottom: 8 }}>
          For principals and district leaders who want their teachers to understand the behavior in the room and know what to do next.
        </p>
        <p style={{ color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.6, marginBottom: 24 }}>
          Here is the whole flow, from what a teacher types to what they walk into class with.
        </p>

        {/* 4-step flow */}
        {STEPS.map((s, i) => (
          <div key={s.n} style={{ display: 'flex', gap: 14, marginBottom: i === STEPS.length - 1 ? 24 : 18, alignItems: 'flex-start' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
              <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'var(--teal)', color: '#fff', fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{s.n}</div>
              {i < STEPS.length - 1 && <div style={{ width: 2, flex: 1, minHeight: 24, background: '#e0dbd3', marginTop: 4 }} />}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>{s.title}</p>
              <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 12 }}>{s.body}</p>
              {s.mock}
            </div>
          </div>
        ))}

        {/* What it does not do */}
        <div className="card" style={{ background: 'var(--teal-light)', border: '1px solid var(--teal)', marginBottom: 24 }}>
          <p style={{ fontWeight: 700, color: 'var(--teal)', fontSize: 15, marginBottom: 10 }}>What it does not do</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {NOT_LIST.map((item) => (
              <span key={item} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--white)', border: '1px solid #d9e6e6', color: 'var(--charcoal)', borderRadius: 20, padding: '6px 12px', fontSize: 13, fontWeight: 600 }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="var(--teal)" strokeWidth="2.5" width="14" height="14"><polyline points="20 6 9 17 4 12" /></svg>
                {item}
              </span>
            ))}
          </div>
        </div>

        {/* Locked school pricing */}
        <h2 style={{ fontSize: 20, marginBottom: 4 }}>School pricing</h2>
        <p style={{ color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.6, marginBottom: 12 }}>
          One invoice, full building access, fundable through Title IV-A.
        </p>
        <div className="card" style={{ marginBottom: 20, padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ background: 'var(--teal-light)' }}>
                  <th style={{ textAlign: 'left', padding: '12px 16px', color: 'var(--teal)', fontWeight: 700 }}>Building size</th>
                  <th style={{ textAlign: 'right', padding: '12px 16px', color: 'var(--teal)', fontWeight: 700 }}>Price</th>
                </tr>
              </thead>
              <tbody>
                {TIERS.map((t, i) => (
                  <tr key={t.students} style={{ borderTop: '1px solid #f0ece6', background: i % 2 ? 'transparent' : 'rgba(0,0,0,0.01)' }}>
                    <td style={{ padding: '12px 16px' }}>{t.students}</td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 700 }}>{t.price}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* CTAs */}
        <div className="grid-2" style={{ marginBottom: 32 }}>
          <button onClick={() => navigate('schools')} className="btn-primary">Get school pricing</button>
          <a href={BOOKING_LINK} target="_blank" rel="noopener noreferrer" className="btn-secondary" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}>
            Book 20 minutes
          </a>
        </div>
      </div>
    </div>
  );
}
